import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
// 2026-06-04 (Fowler etape 9 migration) — bascule de useDialer() au sous-contexte
// cible DialerUIContext. showCelebration vit dans DialerUIContext depuis l'etape 4/3.
import { useDialerUI } from '@/contexts/DialerUIContext';

function fireConfetti() {
  const defaults = {
    startVelocity: 30,
    spread: 360,
    ticks: 80,
    zIndex: 10000,
  };

  const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

  confetti({
    ...defaults,
    particleCount: 60,
    origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
  });
  confetti({
    ...defaults,
    particleCount: 60,
    origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
  });

  setTimeout(() => {
    confetti({
      ...defaults,
      particleCount: 80,
      scalar: 1.2,
      shapes: ['star'],
      colors: ['#22c55e', '#16a34a', '#4ade80', '#fbbf24', '#f59e0b'],
      origin: { x: 0.5, y: 0.4 },
    });
  }, 120);

  setTimeout(() => {
    confetti({
      ...defaults,
      particleCount: 40,
      startVelocity: 15,
      scalar: 0.8,
      origin: { x: 0.5, y: 0.6 },
    });
  }, 300);
}

export default function CelebrationOverlay() {
  const { t } = useTranslation('dialer');
  const { showCelebration, setShowCelebration } = useDialerUI();
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showCelebration) return;

    fireConfetti();

    dismissTimerRef.current = setTimeout(() => {
      setShowCelebration(null);
    }, 4000);

    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [showCelebration, setShowCelebration]);

  const handleDismiss = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
    }
    setShowCelebration(null);
  };

  return (
    <AnimatePresence>
      {showCelebration && (
        <motion.div
          key="celebration-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
          className="flex items-center justify-center bg-black/60 backdrop-blur-sm cursor-pointer"
          onClick={handleDismiss}
          aria-modal="true"
          role="dialog"
          aria-label={t('dialer:celebration.aria')}
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: -10 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24, delay: 0.05 }}
            className="relative flex flex-col items-center gap-4 bg-card rounded-3xl shadow-2xl px-10 py-10 max-w-sm w-full mx-4 text-center overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-green-400/20 blur-2xl" />
            </div>

            <motion.div
              initial={{ rotate: -15, scale: 0.6 }}
              animate={{ rotate: [0, -8, 8, -4, 4, 0], scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center shadow-md"
            >
              <Trophy className="w-10 h-10 text-green-600" strokeWidth={1.75} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl font-bold text-green-600 leading-tight"
            >
              {t('dialer:celebration.title')}
            </motion.h2>

            {(showCelebration.name || showCelebration.company) && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col gap-0.5"
              >
                {showCelebration.name && (
                  <p className="text-lg font-semibold text-foreground">{showCelebration.name}</p>
                )}
                {showCelebration.company && (
                  <p className="text-sm text-muted-foreground">{showCelebration.company}</p>
                )}
              </motion.div>
            )}

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-xs text-muted-foreground/60 mt-1"
            >
              {t('dialer:celebration.dismiss')}
            </motion.p>

            <motion.div
              className="absolute bottom-0 left-0 h-1 bg-green-400 rounded-full"
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: 4, ease: 'linear', delay: 0 }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
